import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchVocabularyGroupById } from "./vocabularyGroupService";
import { useToast } from "../../../../common/toast/ToastContext";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import "./VocabularyGroupDetailPage.css";

const VocabularyGroupDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Helper function to capitalize part of speech
  const capitalizePartOfSpeech = (pos) => {
    if (!pos) return pos;
    return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
  };

  useEffect(() => {
    loadGroupDetail();
  }, [id]);

  const loadGroupDetail = async () => {
    setLoading(true);
    const result = await fetchVocabularyGroupById(id);
    if (result.status === 200) {
      // Handle both possible response structures
      const responseData = result.data;
      let groupData;
      
      if (responseData.status && responseData.data) {
        // Structure: { status: true, data: {...} }
        groupData = responseData.data;
      } else {
        // Direct object structure
        groupData = responseData;
      }
      
      setGroup(groupData);
    } else {
      showToast(result.message || "Failed to load group details", "error");
      navigate("/study/vocabulary/groups");
    }
    setLoading(false);
  };

  const toggleExpanded = (vocabId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(vocabId)) {
      newExpanded.delete(vocabId);
    } else {
      newExpanded.add(vocabId);
    }
    setExpandedCards(newExpanded);
  };

  const playPronunciation = (audioUrl) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(err => {
        console.error("Failed to play audio:", err);
        showToast("Failed to play pronunciation", "error");
      });
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#2196F3';
    }
  };

  const getPartOfSpeechColor = (pos) => {
    switch (pos?.toLowerCase()) {
      case 'noun': return '#9C27B0';
      case 'verb': return '#2196F3';
      case 'adjective': return '#FF5722';
      case 'adverb': return '#795548';
      default: return '#607D8B';
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="vocabulary-group-detail-page">
          <div className="container">
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>Loading group details...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!group) {
    return (
      <>
        <Navbar />
        <div className="vocabulary-group-detail-page">
          <div className="container">
            <div className="error-section">
              <h2>Group not found</h2>
              <button onClick={() => navigate("/study/vocabulary/groups")} className="back-btn">
                Back to Groups
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="vocabulary-group-detail-page">
        <div className="container">
        {/* Header */}
        <div className="header">
          <button onClick={() => navigate("/study/vocabulary/groups")} className="back-btn">
            ← Back to Groups
          </button>
          <div className="group-info">
            <h1>{group.name}</h1>
            {group.description && <p className="description">{group.description}</p>}
            <div className="stats">
              <span className="word-count">
                {group.vocabularies?.length || 0} words
              </span>
            </div>
          </div>
        </div>

        {/* Vocabulary Cards */}
        <div className="vocabulary-section">
          {group.vocabularies && group.vocabularies.length > 0 ? (
            <div className="vocabulary-grid">
              {group.vocabularies.map((vocab) => (
                <div key={vocab.id} className="vocabulary-card">
                  <div className="vocab-header">
                    <div className="word-section">
                      <h3 className="word">{vocab.word}</h3>
                      {vocab.phoneticSpelling && (
                        <span className="phonetic">/{vocab.phoneticSpelling}/</span>
                      )}
                    </div>
                    
                    <div className="tags">
                      {Array.isArray(vocab.partOfSpeech) && vocab.partOfSpeech.length > 0 ? (
                        vocab.partOfSpeech.map((pos, index) => (
                          <span 
                            key={index}
                            className="pos-tag"
                            style={{ 
                              backgroundColor: getPartOfSpeechColor(pos),
                              marginRight: index < vocab.partOfSpeech.length - 1 ? '4px' : '0'
                            }}
                          >
                            {capitalizePartOfSpeech(pos)}
                          </span>
                        ))
                      ) : vocab.partOfSpeech ? (
                        <span 
                          className="pos-tag"
                          style={{ backgroundColor: getPartOfSpeechColor(vocab.partOfSpeech) }}
                        >
                          {capitalizePartOfSpeech(vocab.partOfSpeech)}
                        </span>
                      ) : null}
                      
                      <span 
                        className="difficulty-tag"
                        style={{ backgroundColor: getDifficultyColor(vocab.difficulty) }}
                      >
                        {vocab.difficulty}
                      </span>
                    </div>
                  </div>

                  {vocab.pronunciation && (
                    <button 
                      onClick={() => playPronunciation(vocab.pronunciation)}
                      className="pronunciation-btn"
                      title="Play pronunciation"
                    >
                      🔊 Play
                    </button>
                  )}

                  <div className="definition">
                    <p>{vocab.definition}</p>
                  </div>

                  {/* Expandable Section */}
                  {(vocab.examples?.length > 0 || vocab.synonyms?.length > 0 || vocab.antonyms?.length > 0) && (
                    <div className="expandable-section">
                      <button 
                        onClick={() => toggleExpanded(vocab.id)}
                        className="expand-btn"
                      >
                        {expandedCards.has(vocab.id) ? "Show Less" : "Show More"}
                        <span className={`arrow ${expandedCards.has(vocab.id) ? 'up' : 'down'}`}>
                          ▼
                        </span>
                      </button>

                      {expandedCards.has(vocab.id) && (
                        <div className="expanded-content">
                          {vocab.examples && vocab.examples.length > 0 && (
                            <div className="examples-section">
                              <h4>Examples:</h4>
                              <ul>
                                {vocab.examples.map((example, index) => (
                                  <li key={index}>{example}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {vocab.synonyms && vocab.synonyms.length > 0 && (
                            <div className="synonyms-section">
                              <h4>Synonyms:</h4>
                              <div className="word-list">
                                {vocab.synonyms.map((synonym, index) => (
                                  <span key={index} className="word-chip synonym">
                                    {synonym}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {vocab.antonyms && vocab.antonyms.length > 0 && (
                            <div className="antonyms-section">
                              <h4>Antonyms:</h4>
                              <div className="word-list">
                                {vocab.antonyms.map((antonym, index) => (
                                  <span key={index} className="word-chip antonym">
                                    {antonym}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-vocabulary">
              <div className="no-vocab-icon">📝</div>
              <h3>No vocabulary words</h3>
              <p>This group doesn't contain any vocabulary words yet.</p>
            </div>
          )}
        </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VocabularyGroupDetailPage;